import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2, Download, Trash2, FileText, Menu, X, User, Settings, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';

function Templates({ user, onLogout }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    property_type: 'residential',
    sqft: 0,
    estimated_budget: 0,
    lot_cost: 0,
    sales_price: 0,
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await apiService.getTemplates(user.id);
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    
    try {
      const templateData = {
        ...newTemplate,
        user_id: user.id,
        sqft: parseFloat(newTemplate.sqft),
        estimated_budget: parseFloat(newTemplate.estimated_budget),
        lot_cost: parseFloat(newTemplate.lot_cost),
        sales_price: parseFloat(newTemplate.sales_price),
      };

      const response = await apiService.createTemplate(templateData);
      toast.success('Template created! Now add phases.');
      setIsDialogOpen(false);
      setNewTemplate({
        name: '',
        description: '',
        property_type: 'residential',
        sqft: 0,
        estimated_budget: 0,
        lot_cost: 0,
        sales_price: 0,
      });
      
      // Navigate to template detail to add phases
      navigate(`/template/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to create template');
      console.error(error);
    }
  };

  const handleExportTemplate = async (templateId, templateName) => {
    try {
      const response = await apiService.exportTemplate(templateId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${templateName}_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template exported for bank submission!');
    } catch (error) {
      toast.error('Failed to export template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    
    try {
      await apiService.deleteTemplate(templateId);
      toast.success('Template deleted');
      loadTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold">📄 Templates</h1>
              <p className="text-blue-100 mt-1 text-xs sm:text-sm hidden sm:block">Bid request templates for subcontractors</p>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20"
              >
                Dashboard
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="bg-white/10 hover:bg-white/20 rounded-full"
                  >
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Profile Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Mobile Menu Button */}
            <Button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              variant="ghost"
              size="icon"
              className="md:hidden text-white hover:bg-white/20"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>

          {/* Mobile Navigation Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-2 border-t border-white/20 pt-4">
              <Button
                onClick={() => { navigate('/dashboard'); setIsMobileMenuOpen(false); }}
                variant="ghost"
                className="w-full justify-start text-white hover:bg-white/20"
              >
                Dashboard
              </Button>
              <Button
                onClick={() => { navigate('/profile'); setIsMobileMenuOpen(false); }}
                variant="ghost"
                className="w-full justify-start text-white hover:bg-white/20"
              >
                <Settings className="w-4 h-4 mr-2" />
                Profile
              </Button>
              <Button
                onClick={onLogout}
                variant="ghost"
                className="w-full justify-start text-red-200 hover:bg-white/20"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info Card */}
        <Card className="mb-6 bg-amber-50 border-amber-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <FileText className="w-6 h-6 text-amber-600 mt-1" />
              <div>
                <h3 className="font-bold text-lg text-amber-900 mb-2">📄 What are Bid Request Templates?</h3>
                <p className="text-slate-700 mb-3">
                  Professional templates for requesting competitive bids from subcontractors. Perfect for:
                </p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600 font-bold">•</span>
                    <span>Requesting quotes from electricians, plumbers, HVAC, and other trades</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600 font-bold">•</span>
                    <span>Clearly communicating scope of work and specifications</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600 font-bold">•</span>
                    <span>Getting competitive pricing from multiple contractors</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600 font-bold">•</span>
                    <span>Professional PDF documents you can email or print</span>
                  </li>
                </ul>
                <div className="mt-4 p-3 bg-white rounded-lg border border-amber-200">
                  <p className="text-sm text-slate-600">
                    <strong>Coming Soon:</strong> Pre-built templates for Electrical, Plumbing, HVAC, Framing, Drywall, and more trades.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Bar */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-slate-900">Your Bid Request Templates</h2>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="create-template-button"
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-6 font-semibold shadow-lg hover:shadow-blue-500/20 transition-all"
              >
                <Plus className="w-5 h-5 mr-2" />
                New Template
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">Create Bid Request Template</DialogTitle>
                <DialogDescription>
                  Set up a template for requesting bids from subcontractors
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateTemplate} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="template-name">Template Name</Label>
                  <Input
                    id="template-name"
                    data-testid="template-name-input"
                    placeholder="3000 sqft Single Family Build"
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                    required
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Input
                    id="description"
                    placeholder="Standard single family home template"
                    value={newTemplate.description}
                    onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                    className="h-12"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="property-type">Property Type</Label>
                    <Select
                      value={newTemplate.property_type}
                      onValueChange={(value) => setNewTemplate({ ...newTemplate, property_type: value })}
                    >
                      <SelectTrigger id="property-type" className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residential">Residential</SelectItem>
                        <SelectItem value="commercial">Commercial</SelectItem>
                        <SelectItem value="mixed-use">Mixed-Use</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sqft">Square Feet</Label>
                    <Input
                      id="sqft"
                      data-testid="template-sqft-input"
                      type="number"
                      min="0"
                      placeholder="3000"
                      value={newTemplate.sqft}
                      onChange={(e) => setNewTemplate({ ...newTemplate, sqft: e.target.value })}
                      required
                      className="h-12"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="budget">Estimated Construction Budget ($)</Label>
                    <Input
                      id="budget"
                      data-testid="template-budget-input"
                      type="number"
                      min="0"
                      placeholder="450000"
                      value={newTemplate.estimated_budget}
                      onChange={(e) => setNewTemplate({ ...newTemplate, estimated_budget: e.target.value })}
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lot-cost">Lot Cost ($)</Label>
                    <Input
                      id="lot-cost"
                      type="number"
                      min="0"
                      placeholder="50000"
                      value={newTemplate.lot_cost}
                      onChange={(e) => setNewTemplate({ ...newTemplate, lot_cost: e.target.value })}
                      className="h-12"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sales-price">Estimated Sales Price ($)</Label>
                  <Input
                    id="sales-price"
                    type="number"
                    min="0"
                    placeholder="600000"
                    value={newTemplate.sales_price}
                    onChange={(e) => setNewTemplate({ ...newTemplate, sales_price: e.target.value })}
                    className="h-12"
                  />
                  <p className="text-xs text-slate-500">Used for LTV calculation</p>
                </div>
                <Button
                  type="submit"
                  data-testid="submit-template-button"
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full font-semibold"
                >
                  Create Template & Add Phases
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Templates Grid */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-slate-600">Loading templates...</p>
          </div>
        ) : templates.length === 0 ? (
          <Card className="bg-white rounded-xl border border-slate-100 shadow-sm">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <FileText className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No bid request templates yet</h3>
              <p className="text-slate-500 text-center mb-6 max-w-md">
                Create your first bid request template to streamline subcontractor communications
              </p>
              <Button
                onClick={() => setIsDialogOpen(true)}
                data-testid="empty-state-create-button"
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 font-semibold"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Bid Request Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <Card
                key={template.id}
                data-testid={`template-card-${template.id}`}
                className="bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-blue-200 transition-all"
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-xl font-bold text-slate-900">
                        {template.name}
                      </CardTitle>
                      {template.description && (
                        <CardDescription className="mt-1 text-slate-500">
                          {template.description}
                        </CardDescription>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Square Feet</span>
                      <span className="font-medium text-slate-900">{template.sqft.toLocaleString()} sqft</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Est. Budget</span>
                      <span className="font-bold text-blue-600">${template.estimated_budget.toLocaleString()}</span>
                    </div>
                    {template.lot_cost > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Lot Cost</span>
                        <span className="font-medium text-slate-900">${template.lot_cost.toLocaleString()}</span>
                      </div>
                    )}
                    {template.sales_price > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Sales Price</span>
                        <span className="font-medium text-slate-900">${template.sales_price.toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
                    <Button
                      onClick={() => navigate(`/template/${template.id}`)}
                      data-testid={`view-template-button-${template.id}`}
                      variant="outline"
                      className="flex-1"
                      size="sm"
                    >
                      <Building2 className="w-4 h-4 mr-2" />
                      View
                    </Button>
                    <Button
                      onClick={() => handleExportTemplate(template.id, template.name)}
                      data-testid={`export-template-button-${template.id}`}
                      variant="outline"
                      size="sm"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDeleteTemplate(template.id)}
                      data-testid={`delete-template-button-${template.id}`}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default Templates;
